import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "../../../api/client.js";
import { ReelNextStep } from "./ReelNextStep.js";
import type { ResearchRunReelView } from "./ReelResearchTab.js";
import { ReelUsScoreLine, type ReelUsScoreView } from "./ReelUsScore.js";

/**
 * FIX-053: the Top 30 Reels of a Research Run - the ONE list the user works with (it replaces the wide all-Reels table AND the separate Review list). Each Reel appears once and carries, in one
 * place: creator, Reel link, Relative Performance, compact performance data, thumbnail, the Content Analysis status / action and the two manual decisions. A completed analysis opens directly
 * under ITS Reel (from the already stored review data - viewing it never calls a paid provider). Approve for Use / Reject use the same routes as before; a decision is final.
 */
export interface ReviewItemView {
  readonly reelId: string;
  readonly creatorMedianViews: number | null;
  readonly hasThumbnail: boolean;
  readonly contentAnalysis: {
    readonly status: "acquiring_media" | "media_ready" | "analyzing" | "completed" | "failed";
    readonly failedStage: "media" | "analysis" | null;
    readonly errorMessage: string | null;
    readonly facts: { readonly reel: { readonly songName: string | null } } | null;
    readonly interpretation: {
      readonly summary: string;
      readonly structure: readonly { readonly atSeconds: number | null; readonly description: string }[];
      readonly hook: string | null;
      readonly notableElements: readonly string[];
      readonly likelyAttentionDrivers: readonly { readonly driver: string; readonly rationale: string }[];
      readonly limitations: readonly string[];
    } | null;
    readonly model: string | null;
  } | null;
  readonly decision: { readonly decision: "approve_for_use" | "reject"; readonly decidedInRunId: string; readonly decidedAt: string } | null;
}

/** One stored permanent decision of a Reel of this run, from the decisions route (any deciding run) - including a Reject from an earlier run that the Review list hides. */
interface DecisionView {
  readonly reelId: string;
  readonly decision: "approve_for_use" | "reject";
  readonly decidedInRunId: string;
  readonly decidedAt: string;
}

/** The saved Approved Reel of a Reel (from the Approved Reels list): its id is what the next step (Repurpose) works on. */
interface ApprovedView {
  readonly id: string;
  readonly reelId: string;
  readonly mp4Removed: boolean;
}

const ERROR_MESSAGE: Record<string, string> = {
  already_decided: "This Reel was already decided; a decision is final.",
  review_not_eligible: "Only a Reel that was classified Interesting in this run can be reviewed.",
  thumbnail_completion_not_configured: "Fetching a missing thumbnail is not enabled on this server, so nothing was requested.",
  thumbnail_provider_failed: "The thumbnail could not be fetched. Nothing was stored; try again later.",
  not_eligible: "Only a Reel that was classified Interesting can be analyzed.",
  content_analysis_not_configured: "Content analysis is not enabled on this server, so nothing was fetched or analyzed.",
  content_analysis_in_progress: "A step of this analysis is already running. Try again in a moment.",
};
const dash = (v: number | string | null): string => (v === null ? "—" : String(v));
const contentStatus = (c: ReviewItemView["contentAnalysis"]): string =>
  c === null ? "not analyzed yet" : c.status === "completed" ? "completed" : c.status === "failed" ? `failed (${c.failedStage === "media" ? "video download" : "AI analysis"})` : c.status === "acquiring_media" ? "fetching the video…" : "analyzing…";

export function ReelTop20({ base, personaId, runId, reels, csrfToken, canAct, onApproved, onChanged, navigateTo, usScores }: {
  readonly base: string;
  readonly personaId: string;
  readonly runId: string;
  /** The run's Reels exactly as the API returned them (highest Relative Performance first), at most 30. */
  readonly reels: readonly ResearchRunReelView[];
  readonly csrfToken: string | null;
  readonly canAct: boolean;
  readonly onApproved?: () => void;
  /** Called after an analysis step changed the stored data, so the run's Reel list is read again. */
  readonly onChanged?: () => void;
  /** Opens the video creation screen (default: a normal page navigation). */
  readonly navigateTo?: (url: string) => void;
  /** FIX-080: the US score of each Reel (by reelId) once loaded; absent / null = no US score line (the old card). The caller passes the reels already ranked by it. */
  readonly usScores?: Readonly<Record<string, ReelUsScoreView>> | null;
}) {
  const [items, setItems] = useState<Readonly<Record<string, ReviewItemView>> | null>(null);
  const [decisions, setDecisions] = useState<Readonly<Record<string, DecisionView>> | null>(null);
  const [approved, setApproved] = useState<Readonly<Record<string, ApprovedView>>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const root = `${base}/personas/${encodeURIComponent(personaId)}`;
  const runRoot = `${root}/runs/${encodeURIComponent(runId)}`;

  const load = useCallback(async () => {
    try {
      const [r, d] = await Promise.all([api.get<{ items: readonly ReviewItemView[] }>(`${runRoot}/review`), api.get<{ decisions: readonly DecisionView[] }>(`${runRoot}/decisions`)]);
      // The Approved Reels list only feeds the next-step panel; if it cannot be read the list still works (no panel).
      const a = await api.get<{ srrs: readonly ApprovedView[] }>(`${root}/srrs`).catch(() => ({ srrs: [] as readonly ApprovedView[] }));
      if (mounted.current) {
        setItems(Object.fromEntries(r.items.map((i) => [i.reelId, i])));
        setDecisions(Object.fromEntries(d.decisions.map((x) => [x.reelId, x])));
        setApproved(Object.fromEntries((a.srrs ?? []).map((x) => [x.reelId, x])));
      }
    } catch {
      if (mounted.current) setError("Could not load the Reel details.");
    }
  }, [runRoot, root]);
  useEffect(() => {
    setItems(null);
    setDecisions(null);
    setError(null);
    setOpen(null);
    void load();
  }, [load]);

  const errorText = (e: unknown, fallback: string): string => {
    const body = e instanceof ApiError ? (e.body as { error?: string; message?: string } | null) : null;
    return (body?.error !== undefined ? ERROR_MESSAGE[body.error] : undefined) ?? body?.message ?? fallback;
  };

  const decide = async (reelId: string, decision: "approve_for_use" | "reject") => {
    setBusy(reelId);
    setError(null);
    try {
      if (decision === "approve_for_use") {
        // Approve for Use also creates the SRR (one per Reel, idempotent) through the existing action; nothing else is started.
        await api.post(`${runRoot}/reels/${encodeURIComponent(reelId)}/approve`, csrfToken);
        onApproved?.();
      } else {
        await api.post(`${runRoot}/reels/${encodeURIComponent(reelId)}/review`, csrfToken, { decision });
      }
      await load();
    } catch (e) {
      if (mounted.current) {
        setError(errorText(e, "Could not save the decision."));
        await load();
      }
    } finally {
      if (mounted.current) setBusy(null);
    }
  };

  const completeThumbnail = async (reelId: string) => {
    setBusy(reelId);
    setError(null);
    try {
      await api.post(`${runRoot}/reels/${encodeURIComponent(reelId)}/thumbnail`, csrfToken);
      await load();
    } catch (e) {
      if (mounted.current) setError(errorText(e, "Could not fetch the thumbnail."));
    } finally {
      if (mounted.current) setBusy(null);
    }
  };

  /** The paid content analysis of ONE Reel, started only by this explicit click: each request is one bounded server step, repeated only until it is completed or failed. */
  const analyzeContent = async (reelId: string) => {
    setBusy(reelId);
    setError(null);
    try {
      let guard = 0;
      let status = "";
      while (mounted.current && status !== "completed" && status !== "failed" && guard++ < 4) {
        const res = await api.post<{ contentAnalysis: { status: string } }>(`${runRoot}/reels/${encodeURIComponent(reelId)}/content-analysis`, csrfToken);
        status = res.contentAnalysis.status;
      }
      if (mounted.current) {
        await load();
        onChanged?.();
      }
    } catch (e) {
      if (mounted.current) setError(errorText(e, "Could not analyze the content."));
    } finally {
      if (mounted.current) setBusy(null);
    }
  };

  const entry = (r: ResearchRunReelView) => {
    const item = items?.[r.reelId];
    const interesting = r.analysis !== null && r.analysis.status === "analyzed" && r.analysis.interesting;
    const ca = item?.contentAnalysis ?? null;
    const caStatus = ca !== null ? ca.status : (r.contentAnalysis?.status ?? null);
    const completed = caStatus === "completed";
    // The permanent decision of this Reel, whichever run made it (the Review list hides a Reel rejected in an earlier run, so the decisions route is the source for those).
    const decision = decisions?.[r.reelId] ?? item?.decision ?? null;
    const loaded = items !== null && decisions !== null;
    return (
      <article key={r.reelId} data-testid="top-reel" data-reel={r.reelId} style={{ border: "1px solid var(--border, #ccc)", padding: 12, margin: "12px 0" }}>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <div style={{ width: 120, flex: "none" }}>
            {item?.hasThumbnail === true ? (
              <img data-testid="top-reel-thumbnail" src={`${root}/reels/${encodeURIComponent(r.reelId)}/thumbnail`} alt={`Thumbnail of the Reel by @${r.canonicalAccountId}`} style={{ width: 120, height: "auto" }} />
            ) : (
              <div data-testid="top-reel-no-thumbnail" className="muted">
                Thumbnail unavailable
                {item !== undefined && canAct ? (
                  <div>
                    <button type="button" data-testid="top-reel-get-thumbnail" disabled={busy !== null} onClick={() => void completeThumbnail(r.reelId)}>
                      Get thumbnail
                    </button>
                  </div>
                ) : null}
              </div>
            )}
          </div>
          <div style={{ flex: "1 1 260px", minWidth: 0 }}>
            <h4 style={{ margin: 0 }}>
              @{r.canonicalAccountId} —{" "}
              <span data-testid="top-reel-relative">{r.analysis === null || r.analysis.relativePerformance === null ? "—" : `${r.analysis.relativePerformance.toFixed(2)}×`}</span> the creator's median{" "}
              {r.analysis?.maturity === "early" ? <span data-testid="top-reel-maturity">(early / still accumulating)</span> : null}
            </h4>
            <p style={{ margin: "4px 0", overflowWrap: "anywhere" }}>
              <a href={r.canonicalUrl} target="_blank" rel="noreferrer">
                {r.canonicalUrl}
              </a>
            </p>
            <p style={{ margin: "4px 0" }}>{r.observation.caption ?? "—"}</p>
            <p className="muted" style={{ margin: "4px 0" }} data-testid="top-reel-stats">
              Uploaded {dash(r.observation.uploadedAt)} · Views {dash(r.observation.views)} · Likes {dash(r.observation.likes)} · Comments {dash(r.observation.comments)} · Creator median views {dash(item?.creatorMedianViews ?? null)}
            </p>
            {usScores !== undefined && usScores !== null ? <ReelUsScoreLine score={usScores[r.reelId]} /> : null}
          </div>
        </div>

        <p style={{ margin: "8px 0" }} data-testid="top-reel-content">
          Content analysis: <span data-testid="top-reel-content-status">{interesting ? contentStatus(ca ?? (r.contentAnalysis === null ? null : { ...r.contentAnalysis, errorMessage: null, facts: null, interpretation: null, model: null })) : "not available (not Interesting)"}</span>{" "}
          {interesting && completed ? (
            <button type="button" data-testid="top-reel-content-view" onClick={() => setOpen(open === r.reelId ? null : r.reelId)}>
              {open === r.reelId ? "Hide" : "View"}
            </button>
          ) : interesting && canAct && item !== undefined ? (
            <button type="button" data-testid="top-reel-content-analyze" disabled={busy !== null} onClick={() => void analyzeContent(r.reelId)}>
              {caStatus === "failed" ? "Retry" : "Analyze Content"}
            </button>
          ) : null}
          {ca?.status === "failed" && ca.errorMessage ? <span className="muted"> — {ca.errorMessage}</span> : null}
        </p>

        {interesting && completed && open === r.reelId && ca?.status === "completed" && ca.interpretation !== null ? (
          <div data-testid="top-reel-analysis">
            <section data-testid="top-reel-facts">
              <h5>Facts (from the collected data)</h5>
              <ul>
                <li>Caption: {r.observation.caption ?? "—"}</li>
                <li>Sound: {ca.facts?.reel.songName ?? "—"}</li>
                <li>
                  Views {dash(r.observation.views)} · Creator median views {dash(item?.creatorMedianViews ?? null)} · {r.analysis?.relativePerformance?.toFixed(2) ?? "—"}×
                </li>
              </ul>
            </section>
            <section data-testid="top-reel-ai">
              <h5>AI interpretation (generated by {ca.model ?? "AI"}; not fact)</h5>
              <p>{ca.interpretation.summary}</p>
              {ca.interpretation.hook ? <p>Opening: {ca.interpretation.hook}</p> : null}
              <ol>
                {ca.interpretation.structure.map((x, n) => (
                  <li key={n}>
                    {x.atSeconds !== null ? `${x.atSeconds}s — ` : ""}
                    {x.description}
                  </li>
                ))}
              </ol>
              <p>Notable elements: {ca.interpretation.notableElements.join("; ") || "—"}</p>
              <h6>Likely attention drivers (AI hypotheses)</h6>
              <ul>
                {ca.interpretation.likelyAttentionDrivers.map((d, n) => (
                  <li key={n}>
                    {d.driver}
                    {d.rationale ? ` — ${d.rationale}` : ""}
                  </li>
                ))}
              </ul>
              <h6>Limitations</h6>
              <ul>
                {ca.interpretation.limitations.map((l, n) => (
                  <li key={n}>{l}</li>
                ))}
              </ul>
            </section>
          </div>
        ) : null}

        {decision !== null ? (
          <>
            <p data-testid="top-reel-decision">
              <strong>{decision.decision === "approve_for_use" ? "Approved for Use" : "Rejected"}</strong>
              {decision.decidedInRunId !== runId ? " (decided in an earlier run)" : ""}
            </p>
            {decision.decision === "approve_for_use" && approved[r.reelId] !== undefined ? (
              <ReelNextStep root={root} personaId={personaId} navigateTo={navigateTo} srrId={approved[r.reelId]!.id} mp4Removed={approved[r.reelId]!.mp4Removed} canAct={canAct} csrfToken={csrfToken} />
            ) : null}
          </>
        ) : !loaded ? null : item === undefined ? (
          interesting ? null : <p className="muted" data-testid="top-reel-not-reviewable">Below the Interesting threshold (3.0×) - not available for review.</p>
        ) : canAct ? (
          <p>
            <button type="button" data-testid="top-reel-approve" disabled={busy !== null} onClick={() => void decide(r.reelId, "approve_for_use")}>
              Approve for Use
            </button>{" "}
            <button type="button" data-testid="top-reel-reject" disabled={busy !== null} onClick={() => void decide(r.reelId, "reject")}>
              Reject
            </button>
          </p>
        ) : (
          <p className="muted">Your role cannot decide.</p>
        )}
      </article>
    );
  };

  return (
    <section data-testid="reel-top20">
      <h3>Top 30 Reels</h3>
      <p className="muted">
        The strongest Reels of this Persona's Research Bank, {usScores !== undefined && usScores !== null ? "Reels with on-camera speech first, then without speech, each ranked by US score (highest first; Reels without a score yet at the bottom, by Relative Performance)" : "highest Relative Performance first"}. Your decision on each Reel is final.
      </p>
      {items === null && error === null ? <p className="muted">Loading…</p> : null}
      {reels.map(entry)}
      {error !== null ? (
        <p role="alert" data-testid="top-reel-error">
          {error}
        </p>
      ) : null}
    </section>
  );
}
